import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  ArrowLeft, FileText, CheckCircle, Clock, AlertTriangle, Eye, Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { AdminSubNav, SUBNAV_ANALYTICS } from '../../components/admin/AdminSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminReportsListPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await axios.get(`${API_URL}/api/admin/reports`, { headers, params });
      setReports(res.data);
    } catch (_) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  }, [headers, statusFilter]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_qc':
        return <Badge className="bg-amber-500"><Clock className="h-3 w-3 mr-1" /> Pending QC</Badge>;
      case 'released':
        return <Badge className="bg-green-500"><CheckCircle className="h-3 w-3 mr-1" /> Released</Badge>;
      case 'fix_requested':
        return <Badge className="bg-red-500"><AlertTriangle className="h-3 w-3 mr-1" /> Fix Requested</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const pendingCount = reports.filter(r => r.status === 'pending_qc').length;

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">Performance Reports</h1>
              <p className="text-sm text-zinc-500">Review and approve cleaner reports</p>
            </div>
          </div>
          {pendingCount > 0 && (
            <Badge className="bg-amber-500 text-lg px-4 py-2">
              {pendingCount} Pending Review
            </Badge>
          )}
        </div>
      </header>
      <AdminSubNav section="Analytics" items={SUBNAV_ANALYTICS} />

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Reports</SelectItem>
              <SelectItem value="pending_qc">Pending QC</SelectItem>
              <SelectItem value="released">Released</SelectItem>
              <SelectItem value="fix_requested">Fix Requested</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-zinc-500">{reports.length} reports</span>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
          </div>
        ) : reports.length === 0 ? (
          <Card className="bg-zinc-50 border-dashed">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-zinc-300 mb-4" />
              <p className="text-zinc-500">No reports found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reports.map((report, idx) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold ${
                          report.after_score >= 85 ? 'bg-green-100 text-green-600' :
                          report.after_score >= 65 ? 'bg-amber-100 text-amber-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {Math.round(report.after_score)}
                        </div>
                        <div>
                          <p className="font-semibold text-zinc-900">{report.client_name}</p>
                          <p className="text-sm text-zinc-500">
                            {report.appointment_date} • by {report.cleaner_name}
                          </p>
                          {report.improvement_delta !== null && (
                            <p className={`text-sm font-medium ${
                              report.improvement_delta >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {report.improvement_delta >= 0 ? '+' : ''}{Math.round(report.improvement_delta)} points
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        {report.condition_decline && (
                          <Badge variant="destructive" className="bg-red-100 text-red-700">
                            <AlertTriangle className="h-3 w-3 mr-1" /> Decline
                          </Badge>
                        )}
                        {getStatusBadge(report.status)}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/appointments/${report.appointment_id}/report-review`)}
                          data-testid={`review-btn-${report.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          {report.status === 'pending_qc' ? 'Review' : 'View'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReportsListPage;
